import React, { useContext, useState } from "react";
import PropTypes from "prop-types";
import {
  makeStyles,
  Typography,
  FormControl,
  Select,
  MenuItem,
  Fade,
  Zoom,
  Button,
} from "@material-ui/core";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faGripVertical,
  faPlus,
  faTimes,
  faTrash,
} from "@fortawesome/free-solid-svg-icons";
import { useTranslation } from "react-i18next";
import AuthContext from "../../../../../connections/auth/AuthProvider";
import { grayColor } from "../../../../../assets/jss/material-dashboard-pro-react";

const hierarchyControlsStyle = (theme, clientColor) => ({
  hierarchySection: {
    width: "160px",
    height: "100%", // Span full parent height
    flexShrink: 0,
    backgroundColor: "white",
    borderRadius: "8px",
    padding: "16px",
    border: `1px solid ${grayColor[10]}`,
    boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
    display: "flex",
    flexDirection: "column",
    gap: "12px",
    overflow: "hidden",
    transition: "all 0.3s ease",
  },
  hierarchyTitle: {
    fontSize: "14px",
    fontWeight: "600",
    color: grayColor[1],
    marginBottom: "8px",
    display: "flex",
    alignItems: "center",
    gap: "8px",
  },
  titleIcon: {
    color: clientColor,
    fontSize: "14px",
  },
  selectorContainer: {
    marginBottom: "8px",
  },
  selectorDropdown: {
    "& .MuiSelect-root": {
      fontSize: "12px",
      padding: "8px 12px",
    },
    "& .MuiOutlinedInput-root": {
      borderRadius: "6px",
      backgroundColor: grayColor[13],
      "&:hover .MuiOutlinedInput-notchedOutline": {
        borderColor: clientColor,
      },
      "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
        borderColor: clientColor,
      },
    },
  },
  dimensionsList: {
    display: "flex",
    flexDirection: "column",
    gap: "6px",
    minHeight: "60px",
    flex: 1, // Allow it to grow and fill remaining space
    overflow: "auto", // Handle overflow if needed
  },
  dimensionChip: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "6px 10px",
    backgroundColor: clientColor,
    color: "white",
    borderRadius: "20px", // More pill-like
    fontSize: "10px",
    cursor: "grab",
    userSelect: "none",
    transition:
      "transform 0.3s cubic-bezier(0.4, 0, 0.2, 1), box-shadow 0.2s ease, filter 0.2s ease",
    boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
    border: "none",
    minHeight: "28px", // Fixed height for consistency
    position: "relative",
    "&:hover:not(.dragging):not(.slideUp):not(.slideDown)": {
      transform: "translateY(-1px) scale(1.02)",
      boxShadow: "0 3px 8px rgba(0,0,0,0.12)",
      filter: "brightness(1.08)",
    },
    "&:active": {
      cursor: "grabbing",
    },
    "&.dragging": {
      opacity: 0.5,
      transition: "opacity 0.2s ease",
    },
    "&.slideDown": {
      transform: "translateY(34px) !important",
      transition: "transform 0.3s cubic-bezier(0.4, 0, 0.2, 1) !important",
    },
    "&.slideUp": {
      transform: "translateY(-34px) !important",
      transition: "transform 0.3s cubic-bezier(0.4, 0, 0.2, 1) !important",
    },
  },
  dimensionContent: {
    display: "flex",
    alignItems: "center",
    flex: 1,
    gap: "6px",
  },
  dragHandle: {
    color: "rgba(255,255,255,0.7)",
    fontSize: "8px",
    cursor: "grab",
    "&:hover": {
      color: "white",
    },
  },
  dimensionText: {
    flex: 1,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    fontWeight: "500",
    fontSize: "10px",
  },
  removeButton: {
    cursor: "pointer",
    fontSize: "8px",
    padding: "3px",
    borderRadius: "50%",
    color: "rgba(255,255,255,0.7)",
    transition: "all 0.2s ease",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    width: "16px",
    height: "16px",
    "&:hover": {
      backgroundColor: "rgba(255, 255, 255, 0.2)",
      color: "white",
      transform: "scale(1.1)",
    },
  },
  dropZone: {
    minHeight: "80px",
    border: `2px dashed ${grayColor[8]}`,
    borderRadius: "8px",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    fontSize: "11px",
    color: grayColor[6],
    backgroundColor: grayColor[13],
    padding: "16px",
    textAlign: "center",
    transition: "all 0.3s ease",
    "&.dragOver": {
      borderColor: clientColor,
      backgroundColor: `${clientColor}15`,
      color: clientColor,
      borderStyle: "solid",
      transform: "scale(1.02)",
    },
  },
  extendedDropZone: {
    height: "20px",
    width: "100%",
    position: "relative",
    transition: "all 0.3s ease",
    "&.dragOver": {
      backgroundColor: `${clientColor}10`,
      borderRadius: "4px",
    },
  },
  dropZoneIcon: {
    fontSize: "16px",
    marginBottom: "8px",
    opacity: 0.5,
  },
  emptyStateText: {
    lineHeight: 1.4,
    fontWeight: "500",
  },
  orderNumber: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    width: "16px",
    height: "16px",
    backgroundColor: "rgba(255,255,255,0.25)",
    borderRadius: "50%",
    fontSize: "8px",
    fontWeight: "bold",
    marginRight: "2px",
  },
  clearAllButton: {
    fontSize: "10px",
    padding: "4px 8px",
    backgroundColor: grayColor[11],
    color: grayColor[4],
    border: `1px solid ${grayColor[9]}`,
    borderRadius: "4px",
    textTransform: "none",
    fontWeight: "500",
    minHeight: "28px",
    transition: "all 0.2s ease",
    "&:hover": {
      backgroundColor: grayColor[9],
      color: grayColor[2],
      borderColor: grayColor[7],
    },
    "&:disabled": {
      backgroundColor: grayColor[12],
      color: grayColor[8],
      borderColor: grayColor[10],
      cursor: "not-allowed",
    },
  },
  clearAllIcon: {
    fontSize: "8px",
    marginRight: "4px",
  },
});

const HierarchyControls = ({
  hierarchyOrder,
  availableDimensions,
  dimensionNames,
  draggedItem,
  onAddDimension,
  onRemoveDimension,
  onClearAll,
  onDragStart,
  onDragEnd,
  onDragOver,
  onDrop,
  // eslint-disable-next-line no-unused-vars
  isLoading = false,
}) => {
  const { userDetails } = useContext(AuthContext);
  const clientColor = userDetails.implementation_color;
  const useStyles = makeStyles((theme) =>
    hierarchyControlsStyle(theme, clientColor),
  );
  const classes = useStyles();

  const T_PREFIX = "rgm.cockpit";
  const { t } = useTranslation("translation", { keyPrefix: T_PREFIX });

  const [isDragOver, setIsDragOver] = useState(false);
  const [slideDirections, setSlideDirections] = useState({}); // Track slide direction for each element
  const [currentDropTarget, setCurrentDropTarget] = useState(null); // Track current drop target to prevent loops

  const handleAddDimension = (dimension) => {
    if (dimension) {
      onAddDimension(dimension);
    }
  };

  const handleDragEnter = (e) => {
    e.preventDefault();
    setIsDragOver(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDragOver(false);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    onDragOver(e);
  };

  const handleDrop = (e, index) => {
    e.preventDefault();
    setIsDragOver(false);
    setSlideDirections({});
    setCurrentDropTarget(null);

    // Call the parent's onDrop - this handles the empty zone case
    onDrop(e, index);
  };

  const handleDimensionDragOver = (e, index) => {
    e.preventDefault();
    e.stopPropagation();

    if (!draggedItem || draggedItem.index === index) return;

    // Allow drop
    e.dataTransfer.dropEffect = "move";

    const draggedFromIndex = draggedItem.index;

    // Calculate which elements should slide based on mouse position relative to the element
    const rect = e.currentTarget.getBoundingClientRect();
    const mouseY = e.clientY;
    const elementCenter = rect.top + rect.height / 2;

    let targetIndex = index;

    // If we're hovering over the top half of an element, insert before it
    // If we're hovering over the bottom half, insert after it
    if (mouseY < elementCenter) {
      // Top half - insert before this element
      targetIndex = index;
    } else {
      // Bottom half - insert after this element
      targetIndex = index + 1;
    }

    // Special handling for first and last elements
    if (index === 0 && mouseY < rect.top + 10) {
      // Very top of first element - definitely insert at position 0
      targetIndex = 0;
    } else if (
      index === hierarchyOrder.length - 1 &&
      mouseY > rect.bottom - 10
    ) {
      // Very bottom of last element - insert at the end
      targetIndex = hierarchyOrder.length;
    }

    // Only update if the drop target has actually changed
    if (currentDropTarget === targetIndex) return;

    setCurrentDropTarget(targetIndex);

    setSlideDirections(() => {
      const newSlideDirections = {};

      // Clear all slides first
      for (let i = 0; i < hierarchyOrder.length; i++) {
        newSlideDirections[i] = "";
      }

      // Calculate which elements should slide
      if (draggedFromIndex < targetIndex) {
        // Dragging down: elements between draggedFromIndex+1 and targetIndex-1 should slide up
        for (let i = draggedFromIndex + 1; i < targetIndex; i++) {
          newSlideDirections[i] = "slideUp";
        }
      } else if (draggedFromIndex > targetIndex) {
        // Dragging up: elements between targetIndex and draggedFromIndex-1 should slide down
        for (let i = targetIndex; i < draggedFromIndex; i++) {
          newSlideDirections[i] = "slideDown";
        }
      }

      return newSlideDirections;
    });
  };

  const handleDimensionDrop = (e, index) => {
    e.preventDefault();
    e.stopPropagation();

    setSlideDirections({});
    setCurrentDropTarget(null);

    // Debug logging
    console.log("Drop triggered:", { draggedItem, targetIndex: index });

    // Call the parent's onDrop function
    onDrop(e, index);
  };

  const handleDimensionDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();

    // Don't clear slides immediately - let the next dragOver handle it
    // This prevents flickering when moving between elements
  };

  return (
    <div className={classes.hierarchySection}>
      <Typography className={classes.hierarchyTitle}>
        <FontAwesomeIcon icon={faGripVertical} className={classes.titleIcon} />
        {t("comparative.hierarchy.title")}
      </Typography>

      {/* Dropdown to add dimensions */}
      <div className={classes.selectorContainer}>
        <FormControl
          fullWidth
          variant="outlined"
          size="small"
          className={classes.selectorDropdown}
        >
          <Select
            value=""
            onChange={(e) => handleAddDimension(e.target.value)}
            disabled={availableDimensions.length === 0}
            displayEmpty
          >
            <MenuItem value="">
              <em>
                {availableDimensions.length > 0
                  ? t("comparative.hierarchy.add_dimension")
                  : t("comparative.hierarchy.no_dimensions")}
              </em>
            </MenuItem>
            {availableDimensions.map((dimension) => (
              <MenuItem key={dimension} value={dimension}>
                <FontAwesomeIcon
                  icon={faPlus}
                  style={{ marginRight: "8px", fontSize: "10px" }}
                />
                {dimensionNames[dimension] || dimension}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </div>

      {/* Clear All button */}
      {hierarchyOrder.length > 0 && (
        <Button
          fullWidth
          variant="outlined"
          size="small"
          className={classes.clearAllButton}
          onClick={onClearAll}
        >
          <FontAwesomeIcon icon={faTrash} className={classes.clearAllIcon} />
          {t("comparative.hierarchy.clear_all")}
        </Button>
      )}

      {/* Dimension items with drag and drop */}
      <div
        className={classes.dimensionsList}
        onDragOver={(e) => {
          e.preventDefault();
          e.dataTransfer.dropEffect = "move";
        }}
        onDrop={(e) => {
          e.preventDefault();
          console.log("Container drop triggered:", { draggedItem });

          // If we have a current drop target, use that for the drop
          if (currentDropTarget !== null && draggedItem) {
            console.log("Using currentDropTarget for drop:", currentDropTarget);
            onDrop(e, currentDropTarget);
          }

          setSlideDirections({});
          setCurrentDropTarget(null);
        }}
        onDragLeave={(e) => {
          // Clear slides when leaving the entire container
          const rect = e.currentTarget.getBoundingClientRect();
          const x = e.clientX;
          const y = e.clientY;

          if (
            x < rect.left ||
            x > rect.right ||
            y < rect.top ||
            y > rect.bottom
          ) {
            setSlideDirections({});
            setCurrentDropTarget(null);
          }
        }}
      >
        {hierarchyOrder.length > 0 ? (
          <>
            {/* Extended drop zone above first element */}
            <div
              className={`${classes.extendedDropZone} ${
                currentDropTarget === 0 ? "dragOver" : ""
              }`}
              onDragOver={(e) => {
                e.preventDefault();
                e.stopPropagation();
                if (draggedItem && currentDropTarget !== 0) {
                  setCurrentDropTarget(0);
                  setSlideDirections(() => {
                    const newSlideDirections = {};
                    // All elements slide down when inserting at position 0
                    for (let i = 0; i < draggedItem.index; i++) {
                      newSlideDirections[i] = "slideDown";
                    }
                    return newSlideDirections;
                  });
                }
              }}
            />

            {hierarchyOrder.map((dimension, index) => {
              const isDragging = draggedItem?.dimension === dimension;
              const slideDirection = slideDirections[index] || "";

              return (
                <Zoom
                  key={dimension}
                  in={true}
                  style={{
                    transitionDelay: `${index * 50}ms`,
                  }}
                >
                  <div
                    className={`${classes.dimensionChip} ${
                      isDragging ? "dragging" : ""
                    } ${slideDirection}`}
                    draggable
                    onDragStart={(e) => {
                      onDragStart(e, dimension, index);
                    }}
                    onDragEnd={(e) => {
                      onDragEnd(e);
                      setSlideDirections({});
                      setCurrentDropTarget(null);
                    }}
                    onDragOver={(e) => handleDimensionDragOver(e, index)}
                    onDragLeave={handleDimensionDragLeave}
                    onDrop={(e) => handleDimensionDrop(e, index)}
                  >
                    <div className={classes.dimensionContent}>
                      <span className={classes.orderNumber}>{index + 1}</span>
                      <FontAwesomeIcon
                        icon={faGripVertical}
                        className={classes.dragHandle}
                      />
                      <span className={classes.dimensionText}>
                        {dimensionNames[dimension] || dimension}
                      </span>
                    </div>
                    <FontAwesomeIcon
                      icon={faTimes}
                      className={classes.removeButton}
                      onClick={(e) => {
                        e.stopPropagation();
                        onRemoveDimension(dimension);
                      }}
                    />
                  </div>
                </Zoom>
              );
            })}

            {/* Extended drop zone below last element */}
            <div
              className={`${classes.extendedDropZone} ${
                currentDropTarget === hierarchyOrder.length ? "dragOver" : ""
              }`}
              onDragOver={(e) => {
                e.preventDefault();
                e.stopPropagation();
                if (
                  draggedItem &&
                  currentDropTarget !== hierarchyOrder.length
                ) {
                  setCurrentDropTarget(hierarchyOrder.length);
                  setSlideDirections(() => {
                    const newSlideDirections = {};
                    // Elements after the dragged item slide up when inserting at the end
                    for (
                      let i = draggedItem.index + 1;
                      i < hierarchyOrder.length;
                      i++
                    ) {
                      newSlideDirections[i] = "slideUp";
                    }
                    return newSlideDirections;
                  });
                }
              }}
            />
          </>
        ) : (
          <Fade in={hierarchyOrder.length === 0}>
            <div
              className={`${classes.dropZone} ${isDragOver ? "dragOver" : ""}`}
              onDragOver={handleDragOver}
              onDrop={(e) => handleDrop(e, 0)}
              onDragEnter={handleDragEnter}
              onDragLeave={handleDragLeave}
            >
              <FontAwesomeIcon icon={faPlus} className={classes.dropZoneIcon} />
              <div className={classes.emptyStateText}>
                {t("comparative.hierarchy.select_dimensions_help")}
                <br />
              </div>
            </div>
          </Fade>
        )}
      </div>
    </div>
  );
};

HierarchyControls.propTypes = {
  hierarchyOrder: PropTypes.arrayOf(PropTypes.string).isRequired,
  availableDimensions: PropTypes.arrayOf(PropTypes.string).isRequired,
  dimensionNames: PropTypes.object.isRequired,
  draggedItem: PropTypes.object,
  onAddDimension: PropTypes.func.isRequired,
  onRemoveDimension: PropTypes.func.isRequired,
  onClearAll: PropTypes.func.isRequired,
  onDragStart: PropTypes.func.isRequired,
  onDragEnd: PropTypes.func.isRequired,
  onDragOver: PropTypes.func.isRequired,
  onDrop: PropTypes.func.isRequired,
  isLoading: PropTypes.bool,
};

export default HierarchyControls;
